/**
 * Tab ID utility for correlating requests from the same browser tab.
 *
 * Generates a UUID when a new tab is opened and stores it in sessionStorage.
 * The ID persists across page reloads within the same tab but is unique per tab.
 */

const TAB_ID_STORAGE_KEY = 'x-tab-id';

// crypto.randomUUID is Chrome 92+; older embedded WebViews (e.g. Android Chrome 91) lack it.
export function generateUUID(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Gets or creates a unique tab ID.
 * The ID is stored in sessionStorage so it persists across page reloads
 * but is unique for each browser tab.
 */
export function getTabId(): string {
  let tabId = sessionStorage.getItem(TAB_ID_STORAGE_KEY);

  if (!tabId) {
    tabId = generateUUID();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
  }

  return tabId;
}
